import { useState, useRef, useEffect } from 'react';
import CategoryCard from '@molecules/CategoryCard';
import { getCuisineFilterItems, type CuisineFilterItem } from '@services/cuisineService';

interface CuisineCategoriesProps {
  selectedCuisine: string;
  onSelectCuisine: (cuisineId: string) => void;
}

export const CuisineCategories = ({ selectedCuisine, onSelectCuisine }: CuisineCategoriesProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [cuisines, setCuisines] = useState<CuisineFilterItem[]>([]);

  useEffect(() => {
    getCuisineFilterItems().then(setCuisines).catch(() => {});
  }, []);

  const checkScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      return () => {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [cuisines]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    const scrollAmount = 300;
    scrollContainerRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <section className="mb-12">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Mutfaklar 🍴</h2>
        <p className="text-gray-600">Favori mutfağınızı seçin</p>
      </div>

      <div className="relative overflow-visible">
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
            aria-label="Scroll left"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        <div
          ref={scrollContainerRef}
          className="flex gap-3 md:gap-4 overflow-x-auto overflow-y-visible scrollbar-hide scroll-smooth pt-6 pb-4 px-4 md:px-6"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
          onScroll={checkScroll}
        >
          {cuisines.map((cuisine) => {
            const isSelected = selectedCuisine === cuisine.id;
            return (
              <CategoryCard
                key={cuisine.id}
                id={cuisine.id}
                name={cuisine.name}
                image={cuisine.image}
                isSelected={isSelected}
                onClick={() => onSelectCuisine(cuisine.id)}
              />
            );
          })}
        </div>

        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
            aria-label="Scroll right"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </section>
  );
};
